import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Clock, Package } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers not showing
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom blue marker
const blueIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
      <path fill="#0000FF" d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 13 8 13s8-7.75 8-13c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
      <circle cx="12" cy="8" r="3" fill="#D4FF00"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

// Sample locker locations in São Paulo
const lockerLocations = [
  {
    id: 1,
    name: 'Terras Alphaville - Rio Branco (Particular)',
    address: 'Região do Aeroporto de Rio Branco - BR-364, 143, Rio Branco - AC, 69900-709',
    lat: -9.9233733,
    lng: -67.891751,
    hours: '24h',
  },
  {
    id: 2,
    name: 'Arasuper -  Isaura Parente',
    address: 'R. Isaura Parente, 722 - Isaura Parente, Rio Branco - AC, 69918-270',
    lat: -9.9642147,
    lng: -67.8255267,
    available: 5,
    total: 10,
    hours: '6h às 22h',
  },
];

const MapController = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  
  return null;
};

export const LockerMap = () => {
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [mapCenter, setMapCenter] = useState([-23.5505, -46.6333]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredLockers = lockerLocations.filter(
    (locker) =>
      locker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      locker.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLockerSelect = (locker) => {
    setSelectedLocker(locker);
    setMapCenter([locker.lat, locker.lng]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1 space-y-4">
        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou endereço..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-lokker-blue text-sm"
              data-testid="map-search-input"
            />
          </div>
        </div>

        {/* Locker List */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900">
              Lockers Disponíveis ({filteredLockers.length})
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {filteredLockers.map((locker) => (
              <motion.button
                key={locker.id}
                onClick={() => handleLockerSelect(locker)}
                whileHover={{ backgroundColor: '#F8FAFC' }}
                data-testid={`locker-item-${locker.id}`}
                className={`w-full text-left p-4 border-b border-slate-100 last:border-b-0 transition-colors ${
                  selectedLocker?.id === locker.id ? 'bg-lokker-blue/5 border-l-4 border-l-lokker-blue' : ''
                }`}
              >
                <h4 className="font-semibold text-slate-900 mb-1">{locker.name}</h4>
                <p className="text-sm text-slate-500 mb-2">{locker.address}</p>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1 text-slate-600">
                    <Package className="w-3 h-3" />
                    {locker.available}/{locker.total} livres
                  </span>
                  <span className="flex items-center gap-1 text-slate-600">
                    <Clock className="w-3 h-3" />
                    {locker.hours}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="lg:col-span-2">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden h-[500px]">
          <MapContainer
            center={mapCenter}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            data-testid="locker-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            <MapController center={mapCenter} />
            {filteredLockers.map((locker) => (
              <Marker
                key={locker.id}
                position={[locker.lat, locker.lng]}
                icon={blueIcon}
                eventHandlers={{
                  click: () => handleLockerSelect(locker),
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <h4 className="font-heading font-bold text-slate-900 mb-1">
                      {locker.name}
                    </h4>
                    <p className="text-sm text-slate-600 mb-2">{locker.address}</p>
                    <div className="flex items-center gap-3 text-xs mb-3">
                      <span className="flex items-center gap-1 text-green-600">
                        <Package className="w-3 h-3" />
                        {locker.available} livres
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" />
                        {locker.hours}
                      </span>
                    </div>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${locker.lat},${locker.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-lokker-blue hover:underline"
                    >
                      <Navigation className="w-3 h-3" />
                      Como chegar
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default LockerMap;
